import apiClient from './apiClient';
import { structuredDetail, validationMessage } from './structuredDetail';
import { normalizeGender } from '../../utils/gender';
import { getPrescriptionConfig } from './prescriptionBrandingApi';
import { ADDRESS_FIELDS } from '../../utils/patientAddress';

/**
 * The six structured address fields as a write body fragment. Allowlist over
 * ADDRESS_FIELDS: only a field the caller PROVIDED (!== undefined) is sent
 * (key absent => server leaves it untouched on PATCH); blank/whitespace => null
 * (explicit clear). `address_source` (read-only server map) and `legacy` are
 * never in ADDRESS_FIELDS, so a patient row spread into `src` can't leak them
 * into an `extra = "forbid"` body (422).
 */
const addressFieldsBody = (src) => {
  const out = {};
  ADDRESS_FIELDS.forEach((field) => {
    if (src[field] !== undefined) out[field] = String(src[field] ?? '').trim() || null;
  });
  return out;
};

/**
 * ═══════════════════════════════════════════════════════════════════════
 * Visit API — REST endpoints for visit management
 *
 * Base paths:
 *   POST   /api/organisation/visits             — Register visit
 *   GET    /api/organisation/visits              — List visits (filters)
 *   GET    /api/organisation/visits/daily        — All visits for a day
 *   GET    /api/organisation/visits/doctor/:id   — Doctor queue for a day
 *   GET    /api/organisation/visits/patient/:id  — Patient visit history
 *   PUT    /api/organisation/visits/:id          — Update visit status/type
 *   GET    /api/organisation/patients?mobile=    — Patient lookup
 *   GET    /api/organisation/mem?role=DOCTOR     — Doctor list
 * ═══════════════════════════════════════════════════════════════════════
 */

// ── Visit Registration ───────────────────────────────────────────────

/**
 * Register a new visit (also creates patient if new)
 * POST /api/organisation/visits
 *
 * Response: { patient, visit, queue_number, is_new_patient }
 */
export const registerVisit = async (payload) => {
  try {
    // Doctor-less providers vs OPD (doctor). The backend validator requires
    // nurse_member_id + visit_type=NURSE for a nurse visit and
    // lab_technician_member_id + visit_type=LAB for a lab visit, and FORBIDS
    // visit_doctor_id on either; a non-NURSE/non-LAB visit requires visit_doctor_id.
    const isNurseVisit = payload.visitProviderType === 'NURSE';
    const isLabVisit = payload.visitProviderType === 'LAB';

    const body = {
      mobile: payload.mobile,
      visit_type: isNurseVisit ? 'NURSE' : isLabVisit ? 'LAB' : (payload.visitType || 'REGULAR'),
      relation: payload.relation || 'SELF',
      age: payload.age || null,
      dob: payload.dob || null,
      gender: normalizeGender(payload.gender) || null,
      aadhaar_no: payload.aadhaar_no || null,
      birth_certificate: payload.birth_certificate || null,
      address: payload.address || null,
      ...addressFieldsBody(payload),
      // rx-allergies P2 — `PatientRegistrationRequest` is `extra = "forbid"`,
      // so the key is spelled exactly `allergies`; empty ⇒ null, never "".
      allergies: payload.allergies?.trim() || null,
      payment_mode: payload.paymentMode || null,
      // Printed-token attribution: who advised the work (DOCTOR + name | SELF).
      advised_by: payload.advised_by || 'SELF',
      advised_by_doctor_name: payload.advised_by_doctor_name || null,
      // CONSENT-CAPTURE P1 (D13) — mandatory, `## API CONTRACT`: ONLY the
      // consent VALUE ever reaches the server; the staff read-aloud
      // instruction is frontend-only and never belongs in this body.
      consents: { digital_records: !!payload.consentGranted },
    };

    if (isNurseVisit) {
      // OTC (visit_type=NURSE) is attended by EXACTLY ONE of a nurse OR a doctor
      // (W3 XOR, frozen contract C2): send `visit_doctor_id` for a doctor
      // attendee, `nurse_member_id` for a nurse — never both. `visit_type` stays
      // NURSE either way (no new enum). The backend enforces the XOR (422 on
      // both/neither); the form's validate() guarantees exactly one is set here.
      if (payload.otcAttendeeKind === 'DOCTOR') {
        body.visit_doctor_id = payload.visitDoctorId;
      } else {
        body.nurse_member_id = payload.visitNurseId;
      }
    } else if (isLabVisit) {
      body.lab_technician_member_id = payload.visitLabTechId;
    } else {
      body.visit_doctor_id = payload.visitDoctorId;
    }

    // Appointment link — auto-detected or explicitly set
    if (payload.appointment_id) {
      body.appointment_id = payload.appointment_id;
    }

    // Department-of-care (EPIC A / A2) — stamped at registration, frozen. Only
    // sent when a real department was chosen/auto-filled; omitted for orgs with
    // no departments and for Emergency visits (server assigns the EMERGENCY
    // sentinel). The backend requires it (400) when the org has ≥1 department
    // and it can't auto-resolve — that message surfaces via the store `error`.
    if (payload.department_id) {
      body.department_id = payload.department_id;
    }

    // Existing patient selected — skip patient creation on backend
    if (payload.patient_id) {
      body.patient_id = payload.patient_id;
    } else {
      // New patient — name fields required
      body.first_name = payload.first_name;
      body.last_name = payload.last_name;
      // R88 — check-only duplicate guard; omitted when there is no ABHA.
      if (payload.abhaNumber) body.abha_number = payload.abhaNumber;
      if (payload.scanId) body.scan_id = payload.scanId;   // check-only: lets the server accept a one-word scan name
    }

    const response = await apiClient.post('/api/organisation/visits', body);
    return response.data;
  } catch (error) {
    console.error('[VisitAPI] Error registering visit:', error);
    throw buildApiError(error);
  }
};

// ── Fetch Visits (with optional filters) ─────────────────────────────

/**
 * GET /api/organisation/visits
 *
 * Query params: date, status, doctor_id (all optional)
 * Phase 10: `includeForwarded` — when true and caller is a doctor,
 * the response also surfaces visits the caller has been forwarded out
 * of. Each row carries `viewer_relationship: 'primary' | 'forwarded'`.
 * S8 (EP-4, R3 lab-workflow): `type` ("IPD" | "OPD") and `settlement`
 * ("SETTLED" | "UNSETTLED") — narrow the order-sourced IPD lab entries the
 * LAB scope surfaces; both default ALL server-side when omitted. Rows gain
 * `source`, `is_ipd_lab`, `settlement_status`, `settlement_date`.
 * C1 (round3-wave3 lab-settle-daybucket): IPD-lab rows additionally carry
 * `net_rupees` (number|null — settled collected net; 0 while UNSETTLED;
 * null on non-IPD-lab rows) and `unbilled_amount_rupees` (number|null —
 * UNSETTLED receivable magnitude, memo-only; null once settled / on
 * non-IPD-lab rows). Passed through verbatim (this function returns
 * `response.data` unpicked — no field is dropped); the caller (visitStore /
 * ManageVisitPage) reads them straight off each row, single-source, never
 * re-derived client-side.
 * Response: { visits: [...], count }
 */
export const fetchVisits = async (filters = {}) => {
  try {
    const params = {};
    if (filters.status) params.status = filters.status;
    if (filters.date) params.date = filters.date;
    if (filters.doctor_id) params.doctor_id = filters.doctor_id;
    if (filters.includeForwarded) params.include_forwarded = 'true';
    if (filters.type) params.type = filters.type;
    if (filters.settlement) params.settlement = filters.settlement;

    const response = await apiClient.get('/api/organisation/visits', { params });
    return response.data;
  } catch (error) {
    console.error('[VisitAPI] Error fetching visits:', error);
    throw buildApiError(error);
  }
};

/**
 * Phase 1f — GET /api/organisation/visits/{visit_id}
 * Single-visit fetch; backend returns 404 when the caller isn't on the
 * visit (not 403), so we treat 404 as "not visible" the same as "absent."
 */
export const fetchVisitById = async (visitId) => {
  try {
    const response = await apiClient.get(`/api/organisation/visits/${visitId}`);
    return response.data;
  } catch (error) {
    if (error?.response?.status === 404) return null;
    console.error('[VisitAPI] Error fetching visit by id:', error);
    throw buildApiError(error);
  }
};

// ── Daily Visits ─────────────────────────────────────────────────────

/**
 * GET /api/organisation/visits/daily?date=YYYY-MM-DD&doctor_id=xxx
 *
 * Query params: date (optional), doctor_id (optional — for scoped roles)
 * Response: { visits: [...], count }
 */
export const fetchDailyVisits = async (filters = {}) => {
  try {
    const params = {};
    if (filters.date) params.date = filters.date;
    if (filters.doctor_id) params.doctor_id = filters.doctor_id;
    const response = await apiClient.get('/api/organisation/visits/daily', { params });
    return response.data;
  } catch (error) {
    console.error('[VisitAPI] Error fetching daily visits:', error);
    throw buildApiError(error);
  }
};

// ── Doctor Queue ─────────────────────────────────────────────────────

/**
 * GET /api/organisation/visits/doctor/:doctorId?date=YYYY-MM-DD
 *
 * Response: { visits: [...], count } — sorted by queue_number
 */
export const fetchDoctorQueue = async (doctorId, date) => {
  try {
    const params = {};
    if (date) params.date = date;
    const response = await apiClient.get(
      `/api/organisation/visits/doctor/${doctorId}`,
      { params }
    );
    return response.data;
  } catch (error) {
    console.error('[VisitAPI] Error fetching doctor queue:', error);
    throw buildApiError(error);
  }
};

// ── Patient Visit History ────────────────────────────────────────────

/**
 * GET /api/organisation/visits/patient/:patientId
 *
 * Response: { visits: [...], count } — sorted newest first
 */
export const fetchPatientHistory = async (patientId) => {
  try {
    const response = await apiClient.get(
      `/api/organisation/visits/patient/${patientId}`
    );
    return response.data;
  } catch (error) {
    console.error('[VisitAPI] Error fetching patient history:', error);
    throw buildApiError(error);
  }
};

/**
 * GET /api/organisation/visits/patient/:patientId/tooth-chart-bundle
 *
 * One-shot aggregator powering the dental chart. Returns the most-recent
 * N visits plus per-visit sittings and per-visit metadata in a single
 * response — replaces what the chart used to fetch as 1 + N + N
 * requests. Callers that hit an older backend (404) should fall back to
 * the legacy fetchPatientHistory + per-visit fan-out.
 *
 * Response shape:
 *   {
 *     visits: [...],
 *     sittings_by_visit: { [visit_id]: [SittingSummary] },
 *     metadata_by_visit: { [visit_id]: [MetadataEntry] },
 *     truncated, total_visits, returned_visits, generated_at
 *   }
 */
export const fetchToothChartBundle = async (
  patientId,
  { limit, includeSittings = true, includeMetadata = true } = {},
) => {
  const params = {};
  if (limit != null) params.limit = limit;
  if (includeSittings === false) params.include_sittings = false;
  if (includeMetadata === false) params.include_metadata = false;
  const response = await apiClient.get(
    `/api/organisation/visits/patient/${patientId}/tooth-chart-bundle`,
    { params },
  );
  return response.data;
};

/**
 * GET /api/organisation/visits/patient/:patientId/latest-prescription
 *
 * Single-endpoint replacement for the N+1 metadata loop.
 * Returns { found, medicines, investigations, doctor_notes, ... } or { found: false }.
 */
export const fetchLatestPrescription = async (patientId, excludeVisitId = null, doctorMemberId = null) => {
  try {
    const params = {};
    if (excludeVisitId) params.exclude_visit_id = excludeVisitId;
    if (doctorMemberId) params.doctor_member_id = doctorMemberId;
    const response = await apiClient.get(
      `/api/organisation/visits/patient/${patientId}/latest-prescription`,
      { params }
    );
    return response.data;
  } catch (error) {
    console.error('[VisitAPI] Error fetching latest prescription:', error);
    throw buildApiError(error);
  }
};

// ── Update Visit ─────────────────────────────────────────────────────

/**
 * PUT /api/organisation/visits/:visitId
 *
 * Body (all optional): { visit_doctor_id, visit_type, status }
 * Use for: status changes (IN_PROGRESS, COMPLETED, CANCELLED, SKIPPED), reassignment
 */
export const updateVisit = async (visitId, updates) => {
  try {
    const response = await apiClient.put(
      `/api/organisation/visits/${visitId}`,
      updates
    );
    return response.data;
  } catch (error) {
    console.error('[VisitAPI] Error updating visit:', error);
    throw buildApiError(error);
  }
};

// ── Skip Visit ───────────────────────────────────────────────────────

/**
 * Skip a visit — shorthand for status → SKIPPED.
 * Callable by any role (receptionist, doctor, nurse, admin).
 *
 * @param {string} visitId
 * @param {string} [skippedByRole] - Role of the user who skipped (e.g. 'RECEPTIONIST')
 */
export const skipVisit = async (visitId, skippedByRole) => {
  try {
    const response = await apiClient.put(
      `/api/organisation/visits/${visitId}`,
      { status: 'SKIPPED', skipped_by_role: skippedByRole || 'UNKNOWN' }
    );
    return response.data;
  } catch (error) {
    console.error('[VisitAPI] Error skipping visit:', error);
    throw buildApiError(error);
  }
};

// ── Requeue Skipped Visit ────────────────────────────────────────────

/**
 * PUT /api/organisation/visits/:visitId/requeue
 *
 * Requeues a SKIPPED visit back into QUEUE with smart position reinsertion.
 */
export const requeueVisit = async (visitId) => {
  try {
    const response = await apiClient.put(
      `/api/organisation/visits/${visitId}/requeue`
    );
    return response.data;
  } catch (error) {
    console.error('[VisitAPI] Error requeuing visit:', error);
    throw buildApiError(error);
  }
};

// ── Resume Completed Visit ───────────────────────────────────────────

/**
 * PUT /api/organisation/visits/:visitId/resume
 *
 * Flips a COMPLETED visit back to IN_PROGRESS so the doctor / nurse can
 * finish work on a procedure that became actionable after the visit
 * was closed (typically: a procedure paid at reception after the
 * doctor moved on).
 *
 * Server-side guards (both required, surface as 409 on failure):
 *   - Same-day: visit.visit_date == today (IST)
 *   - Resumable sitting: ≥1 sitting in
 *     {payment_cleared, scheduled, ready, in_progress}
 *
 * Ownership: caller must be the visit's doctor_member_id (or admin).
 *
 * @param {string} visitId - The COMPLETED visit to resume.
 * @param {object} [opts]
 * @param {string} [opts.parkCurrentVisitId] - If the caller has a
 *   different visit currently IN_PROGRESS that they want to park back
 *   to QUEUE before opening this one, pass that visit_id here. Park +
 *   resume run server-side as a sequential pair: if park fails, the
 *   resume is not attempted.
 */
export const resumeVisit = async (visitId, { parkCurrentVisitId } = {}) => {
  try {
    const body = {};
    if (parkCurrentVisitId) body.park_current_visit_id = parkCurrentVisitId;
    const response = await apiClient.put(
      `/api/organisation/visits/${visitId}/resume`,
      body
    );
    return response.data;
  } catch (error) {
    console.error('[VisitAPI] Error resuming visit:', error);
    throw buildApiError(error);
  }
};

// ── Fetch Doctors ────────────────────────────────────────────────────

/**
 * GET /api/organisation/mem?role=DOCTOR
 */
/**
 * Map a raw /mem (StaffMemberResponse[]) payload to the doctor shape the
 * visit store holds. Shared by `fetchDoctors` and the dashboard hydrator so
 * the `doctors` slice is identical whether it arrives via /mem or bundled in
 * /dashboard-summary.
 */
export const mapDoctorsResponse = (data) => {
  const raw = Array.isArray(data)
    ? data
    : data?.member || data?.members || data?.staff || [];

  return raw
    .filter((m) => m.status !== 'INACTIVE' && m.status !== 'DISABLED')
    .map((m) => ({
      doctor_id: m.member_id || m.staff_id || m.id,
      member_id: m.member_id || m.staff_id || m.id,
      name: `${m.first_name || ''} ${m.last_name || ''}`.trim(),
      first_name: m.first_name || '',
      last_name: m.last_name || '',
      display_name: m.display_name || null,
      specialization: m.specialization || null,
      availability: m.availability || 'available',
      status: m.status || 'ACTIVE',
      profile_picture_url: m.profile_picture_url || null,
      qualification: m.qualification || null,
      experience_years: m.experience_years || null,
      about: m.about || null,
      consultation_department: m.consultation_department || null,
      is_publicly_visible: m.is_publicly_visible ?? true,
    }));
};

export const fetchDoctors = async () => {
  try {
    const response = await apiClient.get('/api/organisation/mem', {
      params: { role: 'DOCTOR' },
    });
    return { doctors: mapDoctorsResponse(response.data) };
  } catch (error) {
    console.error('[VisitAPI] Error fetching doctors:', error);
    throw buildApiError(error);
  }
};

// ── Patient Search ───────────────────────────────────────────────────

/**
 * POST /api/organisation/patients — PATIENT-ONLY registration (no visit).
 *
 * RECONCILED (C1 integration): the backend endpoint now exists on
 * feat/C-C1-admit-bed (PatientOnlyRegistrationRequest/Response) and matches
 * the shape below exactly; duplicate SELF-per-mobile returns 400 with the
 * dedup message in `detail` (surfaced via buildApiError's 400 branch).
 * Historical context: the only patient-creation path before this was
 * `registerVisit` (POST /visits), which MANDATES a doctor/nurse/lab-tech and
 * always spawns a visit — unacceptable for the admit desk (S1-AC1: register a
 * new patient inline WITHOUT creating a junk OPD visit). The `/internal/`
 * agent-only `register_patient_from_agent` path is API-key gated (not usable
 * from a session-authenticated org user) and is itself paused/incomplete.
 *
 * Assumed request/response (mirrors `searchPatientByMobile`'s patient shape so
 * the caller can treat the result identically to a search hit):
 *   Request:  { mobile, first_name, last_name, relation, age?, dob?, gender? }
 *   Response: { patient: { patient_id, name, first_name, last_name, mobile,
 *                           age, gender, dob, relation } }
 *
 */
export const createPatient = async (payload) => {
  try {
    const body = {
      mobile: payload.mobile,
      first_name: payload.first_name,
      last_name: payload.last_name,
      relation: payload.relation || 'SELF',
      age: payload.age || null,
      dob: payload.dob || null,
      gender: normalizeGender(payload.gender) || null,
      address: payload.address || null,
      ...addressFieldsBody(payload),
      // CONSENT-CAPTURE P1 (D13) — mandatory, `## API CONTRACT`. ONLY the
      // consent value, never the frontend-only staff instruction string.
      consents: { digital_records: !!payload.consentGranted },
    };
    // R88 — check-only duplicate guard; omitted when there is no ABHA.
    if (payload.abhaNumber) body.abha_number = payload.abhaNumber;
    if (payload.scanId) body.scan_id = payload.scanId;   // check-only: lets the server accept a one-word scan name
    const response = await apiClient.post('/api/organisation/patients', body);
    return response.data?.patient || response.data;
  } catch (error) {
    console.error('[VisitAPI] Error creating patient:', error);
    throw buildApiError(error);
  }
};

/**
 * GET /api/organisation/patients?mobile=XXXXXXXXXX
 *
 * Response: { patients: [...], count }
 */
export const searchPatientByMobile = async (mobile) => {
  try {
    const response = await apiClient.get('/api/organisation/patients', {
      params: { mobile },
    });
    const data = response.data;
    // Return ALL patients linked to this mobile (family member support)
    return data.patients || [];
  } catch (error) {
    if (error.response?.status === 404) {
      return [];
    }
    console.error('[VisitAPI] Error searching patient:', error);
    throw buildApiError(error);
  }
};

/**
 * GET /api/organisation/patients/{patientId}
 *
 * Fetch one patient's full demographics (including address) by ID — unlike
 * `searchPatientByMobile`, always resolves to exactly the patient being
 * viewed, never an ambiguous set of people sharing a phone number. Used by
 * admission-context surfaces (Manage Admission Details tab, IPD summary
 * card, Patient Details dialog) that only carry patient_id/denormalized
 * patient_name and have no address of their own.
 *
 * @param {string} patientId
 * @returns {Promise<object|null>} Patient record, or null if not found
 */
export const getPatient = async (patientId) => {
  try {
    const response = await apiClient.get(`/api/organisation/patients/${patientId}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return null;
    }
    console.error('[VisitAPI] Error fetching patient:', error);
    throw buildApiError(error);
  }
};

// ── Patient Demographics Update ──────────────────────────────────────

/**
 * PATCH /api/organisation/patients/{patientId}
 *
 * Update patient demographics (name, age, gender, dob, address).
 * When visitId is provided, also updates the visit's denormalized snapshot.
 *
 * @param {string} patientId
 * @param {{ firstName?: string, lastName?: string, age?: string, gender?: string, dob?: string, allergies?: string, visitId?: string, address_line?: string, address_town?: string, address_sub_district?: string, address_district?: string, address_state?: string, address_pincode?: string }} data
 * @returns {Promise<object>} Updated patient record
 */
export const updatePatientDemographics = async (patientId, data) => {
  try {
    const body = {};
    if (data.firstName !== undefined) body.first_name = data.firstName;
    if (data.lastName !== undefined) body.last_name = data.lastName;
    if (data.age !== undefined) body.age = data.age || null;
    if (data.gender !== undefined) body.gender = normalizeGender(data.gender) || null;
    // dob: '' = explicit clear (server clears only on ""; null = untouched). Callers send
    // the key only when staff changed it (QA-ADDR-3).
    if (data.dob !== undefined) body.dob = data.dob === '' ? '' : (data.dob || null);
    if (data.relation !== undefined) body.relation = data.relation || null;
    if (data.aadhaarNo !== undefined) body.aadhaar_no = data.aadhaarNo || null;
    if (data.address !== undefined) body.address = data.address || null;
    // Structured address -- callers send ALL six together when the block was
    // edited (server: absent key = untouched, null = clear). Never address_source.
    Object.assign(body, addressFieldsBody(data));
    // rx-allergies P1 — `allergies` is `string | null` (max 500, server-trimmed).
    // KEY ABSENT ⇒ the stored value is untouched; only an explicit empty clears
    // it, and only the Patient Details / registration surfaces ever send that
    // (the prescription pad never sends a blank — Business rule 2).
    if (data.allergies !== undefined) body.allergies = data.allergies || null;
    if (data.visitId) body.visit_id = data.visitId;
    if (data.admissionId) body.admission_id = data.admissionId;

    const response = await apiClient.patch(
      `/api/organisation/patients/${patientId}`,
      body,
    );
    return response.data;
  } catch (error) {
    console.error('[VisitAPI] Error updating patient demographics:', error);
    throw buildApiError(error);
  }
};

/**
 * GET /api/organisation/visits/consultation-stats?date=YYYY-MM-DD
 *
 * Returns per-doctor average consultation durations for waiting-time prediction.
 * Response: { doctors: { [doctorId]: { doctor_name, avg_duration_sec, sample_count, ... } } }
 */
export const fetchConsultationStats = async (date) => {
  try {
    const params = {};
    if (date) params.date = date;
    const response = await apiClient.get(
      '/api/organisation/visits/consultation-stats',
      { params },
    );
    return response.data;
  } catch (error) {
    console.error('[VisitAPI] Error fetching consultation stats:', error);
    // Return empty stats on failure — prediction will fall back to defaults
    return { doctors: {} };
  }
};

// ── Fee Settings ─────────────────────────────────────────────────────

/**
 * GET /api/organisation/settings/fees
 *
 * Response: { doctor_fees: { [doctor_id]: number }, registration_fee_enabled: boolean, registration_fee: number }
 */
export const fetchFeeSettings = async () => {
  try {
    const response = await apiClient.get('/api/organisation/settings/fees');
    return response.data;
  } catch (error) {
    console.error('[VisitAPI] Error fetching fee settings:', error);
    return null;
  }
};

// ── Update Visit Billing ──────────────────────────────────────────────

/**
 * Save billing details for a visit (optional — receptionist fills post-registration)
 * PUT /api/organisation/visits/:visitId/billing
 *
 * Body: { consultation_fee, registration_fee }
 * Response: { visit }
 */
export const updateVisitBilling = async (visitId, { consultationFee, registrationFee }) => {
  try {
    const body = {};
    if (consultationFee !== undefined && consultationFee !== '') body.consultation_fee = Number(consultationFee);
    if (registrationFee !== undefined && registrationFee !== '') body.registration_fee = Number(registrationFee);
    const response = await apiClient.put(`/api/organisation/visits/${visitId}/billing`, body);
    return response.data;
  } catch (error) {
    console.error('[VisitAPI] Error updating billing:', error);
    throw buildApiError(error);
  }
};

/**
 * Procedures-on-New-Visits — preview the (free-follow-up-aware) consultation
 * fee for a returning patient before the visit is created.
 *
 * Response: { base_consultation_fee, effective_consultation_fee,
 *             consultation_fee_waived, waiver_reason, free_followup_window_days,
 *             anchor_date }
 */
export const getRegistrationBillingPreview = async (patientId, doctorMemberId) => {
  try {
    const response = await apiClient.get('/api/organisation/visits/registration-billing-preview', {
      params: { patient_id: patientId, doctor_member_id: doctorMemberId },
    });
    return response.data;
  } catch (error) {
    console.error('[VisitAPI] Error fetching billing preview:', error);
    throw buildApiError(error);
  }
};

// ── Clinic Name Resolution ────────────────────────────────────────────

/**
 * Resolve the clinic display name for token printing.
 * Priority: Rx Branding clinic_name → org profile name → 'Clinic'
 */
export const fetchClinicName = async () => {
  try {
    const [brandingResult, profileResult] = await Promise.allSettled([
      getPrescriptionConfig(),
      apiClient.get('/api/organisation/profile'),
    ]);
    const brandingName = brandingResult.status === 'fulfilled' ? (brandingResult.value?.config?.clinic_name || brandingResult.value?.clinic_name) : null;
    const orgName = profileResult.status === 'fulfilled' ? profileResult.value?.data?.name : null;
    return brandingName || orgName || 'Clinic';
  } catch {
    return 'Clinic';
  }
};

/**
 * Resolve the full clinic branding block for token printing (TOKEN-UPDATE/T1).
 * Priority (mirrors fetchClinicName / prescription_composer_service resolver):
 *   name     — Rx Branding clinic_name → org profile name → 'Clinic'
 *   description — Rx Branding clinic_description ONLY (opt-in, no org-profile fallback)
 *   address  — Rx Branding clinic_address → org profile address
 *   pincode  — Rx Branding clinic_pincode → org profile pincode
 *   phone    — Rx Branding clinic_phone (may be "a, b") → org profile mobile
 */
export const fetchClinicBranding = async () => {
  const fallback = { name: 'Clinic', description: '', address: '', pincode: '', phone: '' };
  try {
    const [brandingResult, profileResult] = await Promise.allSettled([
      getPrescriptionConfig(),
      apiClient.get('/api/organisation/profile'),
    ]);
    const config = brandingResult.status === 'fulfilled' ? (brandingResult.value?.config || brandingResult.value) : null;
    const profile = profileResult.status === 'fulfilled' ? profileResult.value?.data : null;

    return {
      name: config?.clinic_name || profile?.name || 'Clinic',
      description: config?.clinic_description || '',
      address: config?.clinic_address || profile?.address || '',
      pincode: config?.clinic_pincode || profile?.pincode || '',
      phone: config?.clinic_phone || profile?.mobile || '',
    };
  } catch {
    return fallback;
  }
};

// ── Error Helper ─────────────────────────────────────────────────────

function buildApiError(error) {
  const apiError = new Error(error.message || 'An unexpected error occurred');
  apiError.status = error.response?.status;

  if (error.response?.status === 401) {
    apiError.type = 'UNAUTHORIZED';
    apiError.message = 'Session expired. Please login again.';
  } else if (error.response?.status === 403) {
    apiError.type = 'FORBIDDEN';
    apiError.message = 'You do not have permission for this action.';
    // admission-access C-1: keep the machine code (e.g. PROVIDER_OUT_OF_SCOPE)
    // so the screen can map it to its own ratified copy.
    const detail = error.response.data?.detail;
    if (detail && typeof detail === 'object' && detail.code) apiError.code = detail.code;
  } else if (error.response?.status === 404) {
    apiError.type = 'NOT_FOUND';
    apiError.message = error.response.data?.message || 'Resource not found.';
  } else if (error.response?.status === 422) {
    // A2 S7 — doctor↔department linkage: the chosen department isn't valid for
    // the selected doctor (or is unknown/foreign/inactive). Our own
    // DeptScopeViolation handler raises HTTPException(422, detail=<string>),
    // so a STRING `detail` means this really is a dept-scope rejection.
    // FastAPI's own request-validation 422s (e.g. a bad enum value) carry
    // `detail` as an ARRAY of pydantic error objects instead — those are NOT
    // department issues and must not be mislabeled as one (this used to
    // fall through to the DEPT_MISMATCH default for every 422, including
    // e.g. a legacy patient record's non-enum gender value).
    const detail = error.response.data?.detail;
    if (typeof detail === 'string' || error.response.data?.message) {
      apiError.type = 'DEPT_MISMATCH';
      apiError.message = error.response.data?.message || detail
        || 'That department isn’t valid for the selected doctor. Please re-pick the department.';
    } else {
      apiError.type = 'VALIDATION_ERROR';
      apiError.message = validationMessage(detail)
        || 'Please check the highlighted fields and try again.';
    }
  } else if (error.response?.status === 400) {
    // FastAPI HTTPException 400s carry `detail` (e.g. the patient-only
    // registration mobile-dedup message); org handlers carry `message`.
    apiError.type = 'BAD_REQUEST';
    apiError.message = error.response.data?.message
      || (typeof error.response.data?.detail === 'string' ? error.response.data.detail : null)
      || 'Invalid request.';
  } else if (error.response?.status === 409) {
    apiError.type = 'CONFLICT';
    const structured = structuredDetail(error);
    apiError.message = structured?.message
      || (typeof error.response.data?.detail === 'string' ? error.response.data.detail : null)
      || error.response.data?.message || 'Conflicting operation.';
    if (structured?.code) apiError.code = structured.code;
    if (structured?.patient_id) apiError.patient_id = structured.patient_id;
  } else if (error.code === 'ECONNABORTED' || !error.response) {
    apiError.type = 'NETWORK';
    apiError.message = 'Network error. Please check your connection.';
  } else {
    apiError.type = 'UNKNOWN';
    apiError.message = error.response?.data?.message || error.message;
  }

  return apiError;
}

// eslint-disable-next-line import/no-anonymous-default-export
export default {
  registerVisit,
  fetchVisits,
  fetchDailyVisits,
  fetchDoctorQueue,
  fetchPatientHistory,
  fetchToothChartBundle,
  fetchLatestPrescription,
  updateVisit,
  skipVisit,
  requeueVisit,
  fetchDoctors,
  searchPatientByMobile,
  getPatient,
  fetchConsultationStats,
  updatePatientDemographics,
};
